import { Component, inject, OnChanges, WritableSignal, signal, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DxDataGridModule, DxTemplateModule } from 'devextreme-angular';
import { firstValueFrom } from 'rxjs';
import { UtilsService } from '../../services/utils.service';
import { ProjectService, ProjectLoadunitDto, LoadunitVerifyStatus } from '../../shared/generated/backend';
import { SharedModule } from '../../shared/shared.module';
type StatusLookupItem = { value: LoadunitVerifyStatus; labelKey: string };

@Component({
  selector: 'app-assignment-stock',
  imports: [SharedModule, DxDataGridModule, DxTemplateModule, TranslateModule],
  standalone: true,
  templateUrl: './assignment-stock.component.html',
  styleUrl: './assignment-stock.component.scss',
})
export class AssignmentStockComponent implements OnChanges {
  readonly assignmentId = input.required<number>();
  readonly projectNos = input.required<number[]>();

  stocks: WritableSignal<ProjectLoadunitDto[]> = signal([]);
  readonly isLoading: WritableSignal<boolean> = signal(false);

  statusLookupData: StatusLookupItem[] = [
    // Instead of storing final text, store a key
    { value: LoadunitVerifyStatus.Unverified, labelKey: 'ASSIGNMENT.STOCK.STATUS_TYPE.Unverified' },
    { value: LoadunitVerifyStatus.InProgress, labelKey: 'ASSIGNMENT.STOCK.STATUS_TYPE.InProgress' },
    { value: LoadunitVerifyStatus.Verified, labelKey: 'ASSIGNMENT.STOCK.STATUS_TYPE.Verified' },
  ];

  private projectService = inject(ProjectService);
  utilService = inject(UtilsService);
  translateService = inject(TranslateService);

  async ngOnChanges() {
    const projectNos = this.projectNos();
    const assignmentNo = this.assignmentId();
    if (!projectNos || projectNos.length === 0 || !assignmentNo) return;

    this.isLoading.set(true);
    try {
      const results = await Promise.all(
        projectNos.map(projectNo => firstValueFrom(this.projectService.getProjectLoadunits(projectNo)))
      );
      const allLoadunits = results.flat();
      const filtered = allLoadunits.filter(lu => lu.AssignmentNo === assignmentNo);
      this.stocks.set(filtered);
    } catch (error) {
      console.error('Error fetching stock data', error);
      this.stocks.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  displayTranslatedStatus = (item: StatusLookupItem) => {
    if (!item) return '';
    return this.translateService.instant(item.labelKey);
  };

  getTranslatedStatusByValue(value: LoadunitVerifyStatus | null | undefined): string {
    if (!value) {
      return '';
    }

    const statusItem = this.statusLookupData.find(item => item.value === value);
    return statusItem ? this.translateService.instant(statusItem.labelKey) : value;
  }

  getLagerStatusIcon(value: LoadunitVerifyStatus | null | undefined): string {
    switch (value) {
      case LoadunitVerifyStatus.Verified:
        return 'dx-icon-check';
      case LoadunitVerifyStatus.InProgress:
        return 'dx-icon-edit';
      case LoadunitVerifyStatus.Unverified:
        return 'dx-icon-handlehorizontal';
      default:
        return '';
    }
  }

  calculateCreatedDate(data: { StatusCreatedAt?: string }): Date | null {
    return data.StatusCreatedAt ? new Date(data.StatusCreatedAt) : null;
  }

  calculateChangedDate(data: { StatusChangedAt?: string }): Date | null {
    return data.StatusChangedAt ? new Date(data.StatusChangedAt) : null;
  }
}
